import type { ReactNode } from 'react';
import { AccessDenied } from '@/components/AccessDenied';
import { useAuth } from '@/contexts/AuthContext';

type ManageArea = 'contracts' | 'vendors' | 'licenses';

const BACK_LABEL: Record<ManageArea, string> = {
  contracts: 'Back to Contracts',
  vendors: 'Back to Vendors',
  licenses: 'Back to Licenses',
};

interface RequireManageProps {
  area: ManageArea;
  children: ReactNode;
}

/**
 * Route gate for the new/edit pages. Renders nothing while the profile is still loading
 * (so a Manager never flashes the denied view), the page when the role may manage the area,
 * and AccessDenied otherwise.
 */
export function RequireManage({ area, children }: RequireManageProps) {
  const { isLoading, canManageContracts, canManageVendors, canManageLicenses } = useAuth();

  if (isLoading) return null;

  const allowed = {
    contracts: canManageContracts,
    vendors: canManageVendors,
    licenses: canManageLicenses,
  }[area];

  if (!allowed) {
    return <AccessDenied backTo={`/${area}`} backLabel={BACK_LABEL[area]} />;
  }

  return <>{children}</>;
}
